// Empire Apps Chassis · elder · family-mirror
// Read-only screen share from elder to son/daughter via WebRTC P2P.
// DPDP-2023 consent gated. Every session audit-logged.
// Auto-ends after 15 min unless reconfirmed.
// No central signaling server — uses URL-fragment SDP exchange for the prototype path.

import type { LangCode } from "./types.ts";
import { screenReaderAnnounce } from "./a11y.ts";
import { i18n } from "./i18n-strings.ts";
import { makeBilingual } from "./language.ts";

declare const __mirror_brand: unique symbol;
export type MirrorSessionId = string & { readonly [__mirror_brand]: "MirrorSessionId" };

export interface MirrorAuditEntry {
  readonly sessionId: MirrorSessionId;
  readonly startedAt: number;
  readonly endedAt: number | null;
  readonly durationMs: number | null;
  readonly viewerLabel: string;
  readonly endReason: "user-ended" | "auto-timeout" | "viewer-left" | "consent-revoked" | "error";
  readonly consentVersion: string;
}

export interface MirrorConsentResult {
  readonly granted: boolean;
  readonly grantedAt: number;
  readonly viewerLabel: string;
  readonly consentVersion: string;
}

export interface MirrorSession {
  readonly id: MirrorSessionId;
  readonly viewerLabel: string;
  readonly startedAt: number;
  readonly maxDurationMs: number;
  end(reason?: MirrorAuditEntry["endReason"]): Promise<void>;
  reconfirm(): void;
  onRemoteEnd(cb: () => void): void;
}

export interface MirrorOfferPayload {
  readonly sdp: string;
  readonly viewerLabel: string;
  readonly consentVersion: string;
  readonly issuedAt: number;
}

export interface MirrorAnswerPayload {
  readonly sdp: string;
  readonly receivedAt: number;
}

const AUDIT_KEY = "empire.elder.mirror.audit";
const ACTIVE_KEY = "empire.elder.mirror.active";
const DEFAULT_MAX_MS = 15 * 60 * 1000; // 15 min
export const CONSENT_VERSION = "2026-05-elder-mirror-v1";

function uuid(): MirrorSessionId {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID() as MirrorSessionId;
  }
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 10);
  return `${ts}-${rand}` as MirrorSessionId;
}

function safeStorage(): Storage | null {
  if (typeof localStorage === "undefined") return null;
  return localStorage;
}

export function readMirrorAudit(): readonly MirrorAuditEntry[] {
  const s = safeStorage();
  if (!s) return [];
  const raw = s.getItem(AUDIT_KEY);
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as readonly MirrorAuditEntry[];
  } catch {
    return [];
  }
}

function appendAudit(entry: MirrorAuditEntry): void {
  const s = safeStorage();
  if (!s) return;
  const current = readMirrorAudit();
  const next = [...current, entry];
  // Keep last 200 entries
  s.setItem(AUDIT_KEY, JSON.stringify(next.slice(-200)));
}

function updateAuditEnd(
  sessionId: MirrorSessionId,
  endedAt: number,
  reason: MirrorAuditEntry["endReason"],
): void {
  const s = safeStorage();
  if (!s) return;
  const current = readMirrorAudit();
  const idx = current.findIndex((e) => e.sessionId === sessionId);
  if (idx < 0) return;
  const cur = current[idx];
  if (!cur) return;
  const merged: MirrorAuditEntry = {
    ...cur,
    endedAt,
    durationMs: endedAt - cur.startedAt,
    endReason: reason,
  };
  const next = [...current.slice(0, idx), merged, ...current.slice(idx + 1)];
  s.setItem(AUDIT_KEY, JSON.stringify(next));
}

export interface ConsentPromptOptions {
  readonly viewerLabel: string;
  readonly lang: LangCode;
  readonly host: HTMLElement;
}

export async function askMirrorConsent(opts: ConsentPromptOptions): Promise<MirrorConsentResult> {
  return new Promise<MirrorConsentResult>((resolve) => {
    const { viewerLabel, lang, host } = opts;
    const dialog = document.createElement("div");
    dialog.setAttribute("role", "alertdialog");
    dialog.setAttribute("aria-labelledby", "empire-mirror-consent-title");
    dialog.style.cssText = [
      "position:fixed",
      "inset:0",
      "background:rgba(20,16,10,0.78)",
      "display:flex",
      "align-items:center",
      "justify-content:center",
      "z-index:9999",
      "padding:24px",
    ].join(";");
    const card = document.createElement("div");
    card.style.cssText = [
      "background:#fffaeb",
      "border:3px solid #1c1a16",
      "border-radius:18px",
      "padding:32px",
      "max-width:560px",
      "width:100%",
      "box-shadow:0 24px 80px rgba(0,0,0,0.32)",
      "font-family:'Mukta','Crimson Pro',serif",
    ].join(";");
    const titleBilingual = makeBilingual(i18n, "mirror.consent.title", lang);
    const bodyBilingual = makeBilingual(i18n, "mirror.consent.body", lang);
    card.innerHTML = `
      <h2 id="empire-mirror-consent-title" style="font-size:28px;font-weight:700;color:#1c1a16;margin:0 0 12px">${escapeHtml(titleBilingual.primary)}</h2>
      <p style="font-size:18px;color:#5a554d;margin:0 0 24px">${escapeHtml(titleBilingual.secondary)}</p>
      <p style="font-size:20px;color:#1c1a16;margin:0 0 8px;line-height:1.5"><b>${escapeHtml(viewerLabel)}</b> ${escapeHtml(bodyBilingual.primary)}</p>
      <p style="font-size:16px;color:#5a554d;margin:0 0 28px;line-height:1.5">${escapeHtml(bodyBilingual.secondary)}</p>
      <div style="display:flex;gap:14px;flex-wrap:wrap">
        <button type="button" id="empire-mirror-allow" style="flex:1;min-height:72px;background:#b85410;color:#fff;border:3px solid #8e3e08;border-radius:14px;font-size:22px;font-weight:700;cursor:pointer">${escapeHtml(makeBilingual(i18n, "mirror.consent.allow", lang).primary)}</button>
        <button type="button" id="empire-mirror-deny" style="flex:1;min-height:72px;background:transparent;color:#1c1a16;border:3px solid #1c1a16;border-radius:14px;font-size:22px;font-weight:700;cursor:pointer">${escapeHtml(makeBilingual(i18n, "mirror.consent.deny", lang).primary)}</button>
      </div>
    `;
    dialog.appendChild(card);
    host.appendChild(dialog);
    screenReaderAnnounce(titleBilingual.primary + ". " + bodyBilingual.primary, { polite: false, clearAfterMs: 8000 });
    const allow = card.querySelector<HTMLButtonElement>("#empire-mirror-allow");
    const deny = card.querySelector<HTMLButtonElement>("#empire-mirror-deny");
    const finish = (granted: boolean): void => {
      dialog.remove();
      resolve({
        granted,
        grantedAt: Date.now(),
        viewerLabel,
        consentVersion: CONSENT_VERSION,
      });
    };
    allow?.addEventListener("click", () => finish(true));
    deny?.addEventListener("click", () => finish(false));
    allow?.focus();
  });
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    const map: Record<string, string> = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
    return map[c] ?? c;
  });
}

export interface StartMirrorOptions {
  readonly viewerLabel: string;
  readonly lang: LangCode;
  readonly host: HTMLElement;
  readonly maxDurationMs?: number;
  readonly onOffer: (payload: MirrorOfferPayload) => Promise<MirrorAnswerPayload>;
  readonly streamFactory?: () => Promise<MediaStream>;
}

async function defaultStreamFactory(): Promise<MediaStream> {
  if (typeof navigator === "undefined" || !navigator.mediaDevices) {
    throw new Error("mediaDevices unavailable");
  }
  const md = navigator.mediaDevices as MediaDevices & {
    getDisplayMedia?: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
  };
  if (!md.getDisplayMedia) throw new Error("getDisplayMedia unavailable in this browser");
  return md.getDisplayMedia({ video: true, audio: false });
}

export async function startMirrorSession(opts: StartMirrorOptions): Promise<MirrorSession | null> {
  const consent = await askMirrorConsent({
    viewerLabel: opts.viewerLabel,
    lang: opts.lang,
    host: opts.host,
  });
  if (!consent.granted) return null;

  const sessionId = uuid();
  const startedAt = Date.now();
  const maxMs = opts.maxDurationMs ?? DEFAULT_MAX_MS;
  const auditEntry: MirrorAuditEntry = {
    sessionId,
    startedAt,
    endedAt: null,
    durationMs: null,
    viewerLabel: opts.viewerLabel,
    endReason: "user-ended",
    consentVersion: CONSENT_VERSION,
  };
  appendAudit(auditEntry);

  const stream = await (opts.streamFactory ?? defaultStreamFactory)();
  const pc = new RTCPeerConnection({ iceServers: [{ urls: "stun:stun.l.google.com:19302" }] });
  for (const track of stream.getTracks()) pc.addTrack(track, stream);

  const remoteEndedListeners: Array<() => void> = [];
  pc.addEventListener("connectionstatechange", () => {
    if (pc.connectionState === "disconnected" || pc.connectionState === "failed" || pc.connectionState === "closed") {
      for (const cb of remoteEndedListeners) cb();
    }
  });

  const offer = await pc.createOffer({ offerToReceiveAudio: false, offerToReceiveVideo: false });
  await pc.setLocalDescription(offer);

  // Wait for ICE gathering complete
  if (pc.iceGatheringState !== "complete") {
    await new Promise<void>((resolve) => {
      const onChange = (): void => {
        if (pc.iceGatheringState === "complete") {
          pc.removeEventListener("icegatheringstatechange", onChange);
          resolve();
        }
      };
      pc.addEventListener("icegatheringstatechange", onChange);
      // safety timeout
      setTimeout(resolve, 4000);
    });
  }

  const local = pc.localDescription;
  if (!local) {
    pc.close();
    stream.getTracks().forEach((t) => t.stop());
    updateAuditEnd(sessionId, Date.now(), "error");
    return null;
  }

  const offerPayload: MirrorOfferPayload = {
    sdp: local.sdp,
    viewerLabel: opts.viewerLabel,
    consentVersion: CONSENT_VERSION,
    issuedAt: Date.now(),
  };

  let answer: MirrorAnswerPayload;
  try {
    answer = await opts.onOffer(offerPayload);
  } catch {
    pc.close();
    stream.getTracks().forEach((t) => t.stop());
    updateAuditEnd(sessionId, Date.now(), "error");
    return null;
  }

  await pc.setRemoteDescription({ type: "answer", sdp: answer.sdp });

  safeStorage()?.setItem(ACTIVE_KEY, sessionId);

  let ended = false;
  let timeoutHandle: number | null = null;

  const teardown = async (reason: MirrorAuditEntry["endReason"]): Promise<void> => {
    if (ended) return;
    ended = true;
    if (timeoutHandle !== null) {
      clearTimeout(timeoutHandle);
      timeoutHandle = null;
    }
    stream.getTracks().forEach((t) => t.stop());
    pc.close();
    safeStorage()?.removeItem(ACTIVE_KEY);
    updateAuditEnd(sessionId, Date.now(), reason);
    screenReaderAnnounce(
      makeBilingual(i18n, "mirror.session.ended", opts.lang).primary,
      { polite: true, clearAfterMs: 6000 },
    );
  };

  const armTimeout = (): void => {
    if (timeoutHandle !== null) clearTimeout(timeoutHandle);
    timeoutHandle = (typeof window !== "undefined" ? window.setTimeout : setTimeout)(
      () => void teardown("auto-timeout"),
      maxMs,
    ) as unknown as number;
  };
  armTimeout();

  const session: MirrorSession = {
    id: sessionId,
    viewerLabel: opts.viewerLabel,
    startedAt,
    maxDurationMs: maxMs,
    end: (reason = "user-ended") => teardown(reason),
    reconfirm: () => armTimeout(),
    onRemoteEnd: (cb) => {
      remoteEndedListeners.push(() => {
        void teardown("viewer-left");
        cb();
      });
    },
  };
  return session;
}

export function getActiveSessionId(): MirrorSessionId | null {
  const s = safeStorage();
  if (!s) return null;
  const v = s.getItem(ACTIVE_KEY);
  return v ? (v as MirrorSessionId) : null;
}

export function revokeAllMirrorConsent(): void {
  const s = safeStorage();
  if (!s) return;
  s.removeItem(ACTIVE_KEY);
  // Audit entries are immutable per DPDP-2023 audit-trail principle.
}
